import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useStore } from "./useStore";
import {
  initAnalytics,
  isAlloyReady,
  trackEvent,
  eventMap,
  variableMap,
  AnalyticsEventNames,
  AnalyticsVariableNames,
} from "@app/utils/analytics";

let globalTrackingInitialized = false;
let globalPreviousPath = null;

/**
 * Helper to convert value to string
 * @param {any} value - The value to convert
 * @returns {string} The string value
 */
const toString = (value) => String(value);

/**
 * Hook for tracking page views using Adobe Web SDK (Alloy)
 *
 * This hook automatically tracks page views when the route changes and provides
 * methods for custom event tracking. It uses Adobe Alloy (Web SDK) for
 * sending analytics data directly to Adobe Experience Platform.
 *
 * @returns {Object} Tracking methods and utilities
 */
export const usePageTracking = () => {
  const location = useLocation();
  const alloyLoadedRef = useRef(false);

  const ims = useStore((state) => state.ims);

  const initializeAnalyticsAndTrack = async () => {
    if (typeof window !== "undefined" && !alloyLoadedRef.current) {
      try {
        if (!isAlloyReady()) {
          await initAnalytics();
          console.log("Adobe Alloy SDK initialized successfully");
        }
        alloyLoadedRef.current = true;

        trackInitialPageView();
      } catch (error) {
        console.error("Failed to initialize Adobe Alloy SDK:", error);
      }
    }
  };

  const trackInitialPageView = () => {
    const currentPath = location.pathname;

    if (globalPreviousPath === currentPath) {
      return;
    }

    globalPreviousPath = currentPath;

    let additionalData = {
      pageName: currentPath === "/" ? "Home" : currentPath,
      user: { email: ims.profile?.email },
    };

    if (currentPath.startsWith("/stories/")) {
      const storyId = currentPath.split("/")[2];
      additionalData.pageName = `Story View - ${storyId}`;
    } else if (currentPath === "/stories") {
      additionalData.pageName = "Stories List";
    } else if (currentPath === "/projects") {
      additionalData.pageName = "Videos";
    } else if (currentPath === "/projects/new") {
      additionalData.pageName = "Create Video";
    } else if (currentPath.startsWith("/projects/new/")) {
      const pathParts = currentPath.split("/");
      additionalData.pageName = `New Video`;
      additionalData.videoId = pathParts[3];
    } else if (currentPath.startsWith("/projects/")) {
      const pathParts = currentPath.split("/");
      additionalData.pageName = `Video`;
      additionalData.videoId = pathParts[2];
    } else if (currentPath.startsWith("/templates/")) {
      const pathParts = currentPath.split("/");
      const templateId = pathParts[2];
      additionalData.pageName = `Template - ${templateId}`;
    } else if (currentPath === "/templates") {
      additionalData.pageName = "Templates";
    } else if (currentPath === "/admin/mogrt") {
      additionalData.pageName = "Generate Video";
    } else if (currentPath === "/admin/crop-image") {
      additionalData.pageName = "Crop Image";
    } else if (currentPath === "/_copilot") {
      additionalData.pageName = "Copilot";
    } else if (currentPath === "/admin/smart-crop") {
      additionalData.pageName = "Smart Crop";
    }
    trackPageView(additionalData);
  };

  /**
   * Track page view using Adobe Alloy
   *
   * @param {Object} data
   */
  const trackPageView = (data = {}) => {
    if (typeof window !== "undefined" && data?.user?.email) {
      try {
        if (isAlloyReady()) {
          const props = {
            [variableMap[AnalyticsVariableNames.USER_EMAIL]]: toString(
              data.user.email,
            ),
            [variableMap[AnalyticsVariableNames.TIMESTAMP]]: toString(
              new Date().toISOString(),
            ),
            [variableMap[AnalyticsVariableNames.PAGE_NAME]]: toString(
              data.pageName || "Unknown Page",
            ),
            [variableMap[AnalyticsVariableNames.PAGE_URL]]: toString(
              window.location.href,
            ),
            [variableMap[AnalyticsVariableNames.REFERRER_URL]]: toString(
              document.referrer,
            ),
          };

          if (data.videoId) {
            props[variableMap[AnalyticsVariableNames.VIDEO_ID]] = toString(
              data.videoId,
            );
          }

          trackEvent(eventMap[AnalyticsEventNames.PAGE_VIEW], props);
        }
      } catch (error) {
        console.error("Failed to track page view:", error);
      }
    }
  };

  useEffect(() => {
    initializeAnalyticsAndTrack();
  }, []);

  useEffect(() => {
    if (!alloyLoadedRef.current || !isAlloyReady()) {
      return;
    }

    trackInitialPageView();
  }, [location.pathname, ims]);

  return {
    trackPageView,
    trackVideoWizard,
    isTrackingReady: () =>
      alloyLoadedRef.current && typeof window !== "undefined" && isAlloyReady(),
  };
};

/**
 * Track video wizard events using Adobe Alloy
 *
 * @param {Object} videoData
 */
export const trackVideoWizard = (videoData = {}) => {
  if (typeof window !== "undefined" && videoData?.user?.email) {
    try {
      if (isAlloyReady()) {
        console.log("Tracking video wizard event", videoData);

        const props = {
          [variableMap[AnalyticsVariableNames.USER_EMAIL]]: toString(
            videoData.user.email,
          ),
          [variableMap[AnalyticsVariableNames.TIMESTAMP]]: toString(
            new Date().toISOString(),
          ),
        };

        if (videoData.step) {
          props[variableMap[AnalyticsVariableNames.VIDEO_STEP]] = toString(
            videoData.step,
          );
        }

        if (videoData.project?.id) {
          props[variableMap[AnalyticsVariableNames.VIDEO_ID]] = toString(
            videoData.project.id,
          );
        }

        if (videoData.project?.name) {
          props[variableMap[AnalyticsVariableNames.VIDEO_NAME]] = toString(
            videoData.project.name,
          );
        }

        if (videoData.project?.videoPath) {
          props[variableMap[AnalyticsVariableNames.VIDEO_PATH]] = toString(
            videoData.project.videoPath,
          );
        }

        if (videoData.project?.expressId) {
          props[variableMap[AnalyticsVariableNames.EXPRESS_ID]] = toString(
            videoData.project.expressId,
          );
        }

        if (videoData.customer?.name) {
          props[variableMap[AnalyticsVariableNames.CUSTOMER_NAME]] = toString(
            videoData.customer.name,
          );
        }

        if (videoData.customer?.industry) {
          props[variableMap[AnalyticsVariableNames.CUSTOMER_INDUSTRY]] =
            toString(videoData.customer.industry);
        }

        if (videoData.customer?.drNumber) {
          props[variableMap[AnalyticsVariableNames.CUSTOMER_DR_NUMBER]] =
            toString(videoData.customer.drNumber);
        }

        if (videoData.customer?.accountName && videoData.customer.accountName.trim()) {
          props[variableMap[AnalyticsVariableNames.ACCOUNT_NAME]] = toString(videoData.customer.accountName);
        }

        const eventName = videoData.isConversion
          ? AnalyticsEventNames.VIDEO_WIZARD_CONVERSION
          : AnalyticsEventNames.VIDEO_WIZARD_STEP;

        trackEvent(eventMap[eventName], props);
      }
    } catch (error) {
      console.error("Failed to track video event:", error);
    }
  }
};
